import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Building2, ArrowLeft, Save, Sun, Moon, Palette, Plus, X, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clearBrandSettingsCache } from '../../hooks/useBrandSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default brand colors
const DEFAULT_BRAND_COLORS = [
  '#1E3A8A', '#3B82F6', '#EAB308', '#000000', '#FFFFFF',
  '#22C55E', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4'
];

export const CompanySettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    primary_color: '#2563eb',
    secondary_color: '#1e40af',
    brand_colors: DEFAULT_BRAND_COLORS,
    default_font: 'Inter',
    marketing_channels: ['facebook', 'instagram', 'linkedin', 'twitter', 'blog'],
    light_mode_enabled: false
  });
  const [newColor, setNewColor] = useState('#000000');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setFormData({
        company_name: response.data.company_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        website: response.data.website || '',
        primary_color: response.data.primary_color || '#2563eb',
        secondary_color: response.data.secondary_color || '#1e40af',
        brand_colors: response.data.brand_colors || DEFAULT_BRAND_COLORS,
        default_font: response.data.default_font || 'Inter',
        marketing_channels: response.data.marketing_channels || ['facebook', 'instagram', 'linkedin', 'twitter', 'blog'],
        light_mode_enabled: response.data.light_mode_enabled || false
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.patch(`${API}/settings/company`, formData);
      // Phase 6: Clear brand settings cache so Marketing Hub gets fresh data
      clearBrandSettingsCache();
      toast.success('Company settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const addBrandColor = () => {
    if (!formData.brand_colors.includes(newColor)) {
      setFormData({...formData, brand_colors: [...formData.brand_colors, newColor]});
      setNewColor('#000000');
    } else {
      toast.error('Color already exists');
    }
  };

  const removeBrandColor = (index) => {
    const newColors = formData.brand_colors.filter((_, i) => i !== index);
    setFormData({...formData, brand_colors: newColors});
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Company Settings</h1>
              <p className="text-muted-foreground">Manage your company profile and branding</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Basic information about your company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  placeholder="RhinoShield Florida"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contact@rhinoshieldflorida.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://rhinoshieldflorida.com"
                />
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle>Business Address</CardTitle>
              <CardDescription>Your primary business location</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Street Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Tampa"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    placeholder="FL"
                  />
                </div>
                <div>
                  <Label>ZIP Code</Label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    placeholder="33601"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
              <CardDescription>Customize your platform&apos;s appearance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex items-center space-x-4 mt-2">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                      className="h-12 w-20 rounded cursor-pointer border-2 border-border"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                      placeholder="#2563eb"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex items-center space-x-4 mt-2">
                    <input
                      type="color"
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                      className="h-12 w-20 rounded cursor-pointer border-2 border-border"
                    />
                    <Input
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                      placeholder="#1e40af"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-accent/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Color Preview:</p>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <div 
                      className="h-20 rounded-lg shadow-md flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: formData.primary_color }}
                    >
                      Primary
                    </div>
                  </div>
                  <div className="flex-1">
                    <div 
                      className="h-20 rounded-lg shadow-md flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: formData.secondary_color }}
                    >
                      Secondary
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Phase 6: Brand Kit for Marketing Hub */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-purple-500" />
                Brand Kit (Marketing Hub)
                <Badge variant="secondary" className="ml-2">
                  <Link2 className="h-3 w-3 mr-1" />
                  Syncs to Marketing
                </Badge>
              </CardTitle>
              <CardDescription>
                These settings sync to Marketing Hub&apos;s Content Studio and Dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Brand Colors Palette */}
              <div>
                <Label className="mb-3 block">Brand Color Palette</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  These colors appear in the Content Studio design editor
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.brand_colors.map((color, idx) => (
                    <div key={idx} className="relative group">
                      <div 
                        className="w-10 h-10 rounded-lg border-2 border-border cursor-pointer"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                      <button
                        type="button"
                        onClick={() => removeBrandColor(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    className="h-10 w-16 rounded cursor-pointer border-2 border-border"
                  />
                  <Input
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    className="w-28"
                    placeholder="#000000"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addBrandColor}>
                    <Plus className="h-4 w-4 mr-1" /> Add Color
                  </Button>
                </div>
              </div>

              {/* Default Font */}
              <div>
                <Label>Default Font</Label>
                <select
                  value={formData.default_font}
                  onChange={(e) => setFormData({...formData, default_font: e.target.value})}
                  className="w-full mt-1 h-10 rounded-md border border-border bg-background px-3"
                >
                  <option value="Inter">Inter</option>
                  <option value="Arial">Arial</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Verdana">Verdana</option>
                </select>
              </div>

              {/* Marketing Channels */}
              <div>
                <Label className="mb-2 block">Default Marketing Channels</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  These appear as options in the Scheduler and Content Studio
                </p>
                <div className="flex flex-wrap gap-2">
                  {['facebook', 'instagram', 'linkedin', 'twitter', 'blog', 'google_business', 'tiktok', 'youtube'].map(channel => (
                    <label key={channel} className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-accent">
                      <input
                        type="checkbox"
                        checked={formData.marketing_channels.includes(channel)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, marketing_channels: [...formData.marketing_channels, channel]});
                          } else {
                            setFormData({...formData, marketing_channels: formData.marketing_channels.filter(c => c !== channel)});
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm capitalize">{channel.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-xs">
                <p className="font-medium text-blue-800 dark:text-blue-300 mb-1">Changes sync automatically to:</p>
                <ul className="text-blue-700 dark:text-blue-400 space-y-0.5 list-disc list-inside">
                  <li>Content Studio → Brand colors in design editor</li>
                  <li>Scheduler → Channel dropdown options</li>
                  <li>Marketing Dashboard → Company branding</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Feature Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Feature Settings</CardTitle>
              <CardDescription>Control platform features and UI options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border opacity-60">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-600">
                    <Moon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Theme Mode</p>
                    <p className="text-xs text-muted-foreground">
                      Dark mode only (platform policy)
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={false} 
                  disabled={true}
                  title="Light mode is disabled platform-wide"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 p-3 rounded-lg">
                🌙 Dark mode is enforced platform-wide for consistency and stability. Light mode support will be enabled in a future update.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={() => navigate('/settings')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};